if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    renderRequestId = 0;
    priceObserver = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.productForm?.querySelector('input[name="quantity"]') ||
        this.querySelector('m-quantity-input input');
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
      this.priceObserver?.disconnect();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      const initialVariant = this.variantPicker?.initializeBestellmengeVariant();
      const initialBestellmengeControl = this.variantPicker?.getBestellmengeControlForVariant(initialVariant);
      if (initialVariant) {
        this.currentVariant = initialVariant;
      }
      if (initialVariant && initialBestellmengeControl) {
        initialBestellmengeControl.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();
      this.quantityInputs.forEach((input) => {
        input.addEventListener('input', this.updateTotalPrice.bind(this));
        input.addEventListener('change', this.updateTotalPrice.bind(this));
      });
      this.updateVariantMetafieldContent(this.currentVariant);
      this.updateTotalPrice();
      this.observePriceChanges();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedVariant } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      const optionGroup = target.closest('.m-product-option__inner');
      const isBestellmengeChange = optionGroup?.dataset.optionName?.trim().toLowerCase() === 'bestellmenge';
      const requestUrl = this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
        selectedVariant?.id
      );

      this.renderProductInfo({
        requestUrl,
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode, selectedVariant?.id)
          : this.handleUpdateProductInfo(productUrl, this.viewMode, !isBestellmengeChange, selectedVariant?.id),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const renderRequestId = ++this.renderRequestId;

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          if (renderRequestId !== this.renderRequestId) return;
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      if (variantId) {
        params.push(`variant=${encodeURIComponent(variantId)}`);
      }

      return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode, resetBestellmenge = false, selectedVariantId = null) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variantPicker = html.querySelector('variant-picker');
        const selectedVariant = this.getVariantById(variantPicker, selectedVariantId)
          || this.getVariantFromPickerElement(variantPicker)
          || this.getSelectedVariant(variantPicker);
        let variant = selectedVariant;
        if (resetBestellmenge && variantPicker) {
          variant = this.getFirstBestellmengeVariantFromPicker(variantPicker, selectedVariant)
            || selectedVariant;
        }

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(variant);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.currentVariant = variant;

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );

        this.updateVariantMetafieldContent(variant, variantPicker);
        this.updateTotalPrice();

        try {
          this.updateMedia(variant);
        } catch (error) {
          console.error('[ProductInfo] Unable to update variant media', error);
        }

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );

        this.updateTotalPrice();
        requestAnimationFrame(() => this.updateTotalPrice());
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode, selectedVariantId = null) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variantPicker = productInfo.querySelector('variant-picker');
        const selectedVariant = this.getVariantById(variantPicker, selectedVariantId)
          || this.getVariantFromPickerElement(variantPicker)
          || this.getSelectedVariant(variantPicker);
        const variant = this.getFirstBestellmengeVariantFromPicker(variantPicker, selectedVariant)
          || selectedVariant;
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.matches?.('variant-picker')
        ? productInfoNode.querySelector('[data-selected-variant]')?.textContent
        : productInfoNode.querySelector('variant-picker [data-selected-variant]')?.textContent;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    getVariantFromPickerElement(variantPicker) {
      if (!variantPicker) return null;

      const variantsElement = variantPicker.querySelector('[data-product-variants]');
      const variants = variantsElement ? JSON.parse(variantsElement.textContent) : [];
      const groups = Array.from(variantPicker.querySelectorAll('.m-product-option__inner'));
      const selectedValues = groups.map((group) => {
        const select = group.querySelector('select');
        const radio = group.querySelector('input[type="radio"]:checked');
        return select?.value || radio?.value || '';
      });

      if (selectedValues.some((value) => !value)) return null;
      return variants.find((variant) => {
        return variant.options.every((value, index) => value === selectedValues[index]);
      }) || null;
    }

    getVariantById(variantPicker, variantId) {
      if (!variantPicker || !variantId) return null;

      const variantsElement = variantPicker.querySelector('[data-product-variants]');
      const variants = variantsElement ? JSON.parse(variantsElement.textContent) : [];
      return variants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    getFirstBestellmengeVariantFromPicker(variantPicker, selectedVariant) {
      if (!variantPicker) return null;

      const variantsElement = variantPicker.querySelector('[data-product-variants]');
      const variants = variantsElement ? JSON.parse(variantsElement.textContent) : [];
      const groups = Array.from(variantPicker.querySelectorAll('.m-product-option__inner'));
      const quantityIndex = groups.findIndex((group) => {
        return (group.dataset.optionName || '').trim().toLowerCase() === 'bestellmenge';
      });
      if (quantityIndex === -1) return selectedVariant;

      const selectedValues = selectedVariant?.options || groups.map((group) => {
        const select = group.querySelector('select');
        const radio = group.querySelector('input[type="radio"]:checked');
        return select?.value || radio?.value || '';
      });
      const controls = Array.from(groups[quantityIndex].querySelectorAll('select, input[type="radio"]'));
      const availableVariants = variants.filter((variant) => variant.available);

      for (const control of controls) {
        if (control.disabled) continue;
        const variant = availableVariants.find((candidate) => {
          return candidate.options.every((value, index) => {
            return index === quantityIndex || !selectedValues[index] || value === selectedValues[index];
          }) && candidate.options[quantityIndex] === control.value;
        });
        if (variant) return variant;
      }

      return selectedVariant;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(variant) {
      this.variantPicker?.setVariant(variant);
    }

    updateTotalPrice() {
      if (!this.currentVariant?.price) return;

      const quantity = Number.parseInt(this.quantityInput?.value, 10) || 1;
      const tieredUnitPrice = this.getTieredUnitPrice();
      const unitPrice = tieredUnitPrice ?? this.currentVariant.price;
      const totalPrice = unitPrice * quantity;


      // new code per price

      // Update per-piece price
const formattedUnitPrice = formatMoney(
  unitPrice,
  window.MinimogSettings.money_format
);

this.querySelectorAll('[data-price-per-piece-value]').forEach((element) => {
  if (element.textContent.trim() !== formattedUnitPrice) {
    element.textContent = formattedUnitPrice;
  }
});


      const priceElements = this.querySelectorAll(
        '#Price-' + this.dataset.sectionId + ' .m-price__regular > .m-price-item--regular, ' +
        '#Price-' + this.dataset.sectionId + ' .m-price__sale > .m-price-item--sale, ' +
        '.product-block-group--price .m-price__regular > .m-price-item--regular, ' +
        '.product-block-group--price .m-price__sale > .m-price-item--sale, ' +
        '.main-product__block-price .m-price__regular > .m-price-item--regular, ' +
        '.main-product__block-price .m-price__sale > .m-price-item--sale'
      );

      priceElements.forEach((element) => {
        const formattedPrice = formatMoney(totalPrice, window.MinimogSettings.money_format);
        if (element.textContent !== formattedPrice) element.textContent = formattedPrice;
      });
      this.querySelectorAll('.price-per-item--current').forEach((element) => {
        const formattedPrice = formatMoney(unitPrice, window.MinimogSettings.money_format);
        if (element.textContent !== formattedPrice) element.textContent = formattedPrice;
      });
      this.updateTieredPricingHighlight();
    }

    observePriceChanges() {
      const priceContainer = this.querySelector(
        `#Price-${this.dataset.sectionId}, .product-block-group--price, .main-product__block-price`
      );
      if (!priceContainer || typeof MutationObserver === 'undefined') return;

      this.priceObserver?.disconnect();
      this.priceObserver = new MutationObserver(() => this.updateTotalPrice());
      this.priceObserver.observe(priceContainer, { childList: true, subtree: true });
    }

    getTieredUnitPrice() {
      const container = this.querySelector('[data-variant-tiered-pricing]');
      if (!container || !this.tieredPricingActive) return null;

      const quantity = Number.parseInt(this.quantityInput?.value, 10) || 1;
      const cards = Array.from(container.querySelectorAll('.m-variant-tiered-pricing__card'));
      const card = cards
        .find((item) => {
          const minimum = Number(item.dataset.minimum);
          const maximum = item.dataset.maximum ? Number(item.dataset.maximum) : Infinity;
          return quantity >= minimum && quantity <= maximum;
        }) || cards[0];

      const price = Number(card?.dataset.priceCents);
      return Number.isFinite(price) ? price : null;
    }

    updateVariantMetafieldContent(variant, variantPicker = this.variantPicker) {
      const content = this.querySelector('[data-variant-metafield-content]');
      const metafieldsElement = variantPicker?.querySelector('[data-variant-metafields]');
      let variantMetafields = variantPicker?.variantMetafields || [];
      if (metafieldsElement) {
        try {
          variantMetafields = JSON.parse(metafieldsElement.textContent);
        } catch (error) {
          console.error('Unable to read variant metafields', error);
          variantMetafields = [];
        }
      }
      const variantData = variantMetafields.find(
        (item) => String(item.id) === String(variant?.id)
      );
      if (!content) return;
      if (!variantData) {
        this.tieredPricingActive = false;
        content.querySelector('[data-variant-tiered-pricing]')?.replaceChildren();
        content.querySelector('[data-variant-tiered-pricing]')?.classList.add('m:hidden');
        content.querySelector('[data-variant-fabric-info]')?.replaceChildren();
        content.querySelector('[data-variant-fabric-info]')?.classList.add('m:hidden');
        content.querySelector('[data-variant-artwork-upload]')?.classList.add('m:hidden');
        this.updateTotalPrice();
        return;
      }

      this.renderTieredPricing(content, variantData);
      this.renderFabricInfo(content, variantData);

      const artworkUpload = content.querySelector('[data-variant-artwork-upload]');
      artworkUpload?.classList.toggle('m:hidden', variantData.requires_artwork_upload !== true);
    }

    renderTieredPricing(content, variantData) {
      const container = content.querySelector('[data-variant-tiered-pricing]');
      if (!container) return;
      container.replaceChildren();

      let tiers = variantData.tiered_pricing;
      if (typeof tiers === 'string') {
        try {
          tiers = JSON.parse(tiers);
        } catch (error) {
          tiers = null;
        }
      }
      if (!tiers || typeof tiers !== 'object' || Array.isArray(tiers)) {
        this.tieredPricingActive = false;
        container.classList.add('m:hidden');
        return;
      }

      const thresholds = Object.keys(tiers)
        .map(Number)
        .filter(Number.isFinite)
        .sort((first, second) => first - second);
      if (!thresholds.length) {
        this.tieredPricingActive = false;
        container.classList.add('m:hidden');
        return;
      }

      const minimumQuantity = Number(variantData.tiered_pricing_min_qty) || 0;
      const baseMinimum = minimumQuantity > 1 ? minimumQuantity : 1;
      const cards = [{
        minimum: baseMinimum,
        maximum: thresholds[0] - 1,
        price: Number(variantData.price) / 100,
      }];

      thresholds.forEach((threshold, index) => {
        cards.push({
          minimum: threshold,
          maximum: thresholds[index + 1] ? thresholds[index + 1] - 1 : null,
          price: Number(tiers[String(threshold)] ?? tiers[threshold]),
        });
      });

      cards.forEach((card, index) => {
        const element = document.createElement('div');
        element.className = 'm-variant-tiered-pricing__card';
        element.dataset.minimum = String(card.minimum);
        element.dataset.priceCents = String(Math.round(card.price * 100));
        if (card.maximum !== null) element.dataset.maximum = String(card.maximum);

        const range = document.createElement('span');
        range.className = 'm-variant-tiered-pricing__range';
        range.textContent = index === 0 && card.minimum === 1 && card.maximum === 1
          ? '1 Stück'
          : card.maximum === null
            ? `Über ${card.minimum} Stück`
            : `${card.minimum}-${card.maximum} Stück`;

        const price = document.createElement('span');
        price.className = 'm-variant-tiered-pricing__price';
        price.textContent = formatMoney(Math.round(card.price * 100), window.MinimogSettings.money_format);
        element.addEventListener('click', () => {
          this.quantityInputs.forEach((input) => {
            input.value = String(card.minimum);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        element.append(range, price);
        container.append(element);
      });

      container.classList.remove('m:hidden');
      this.tieredPricingActive = true;

      const firstCard = container.querySelector('.m-variant-tiered-pricing__card');
      if (firstCard && this.quantityInputs.length > 0) {
        const quantity = Number(firstCard.dataset.minimum);

        this.quantityInputs.forEach((input) => {
          input.value = String(quantity);
        });
      }

      this.updateTotalPrice();
    }

    renderFabricInfo(content, variantData) {
      const container = content.querySelector('[data-variant-fabric-info]');
      if (!container) return;
      container.replaceChildren();

      [['Material', variantData.fabric_composition], ['Print technique', variantData.print_technique]]
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim())
        .forEach(([label, value]) => {
          const line = document.createElement('span');
          line.className = 'm-variant-fabric-info__line';
          line.textContent = `${label}: ${value}`;
          container.append(line);
        });

      container.classList.toggle('m:hidden', !container.children.length);
    }

    updateTieredPricingHighlight() {
      const container = this.querySelector('[data-variant-tiered-pricing]');
      if (!container || !this.tieredPricingActive) return;
      const quantity = Number.parseInt(this.quantityInput?.value, 10) || 1;
      const cards = Array.from(container.querySelectorAll('.m-variant-tiered-pricing__card'));
      const matchingCard = cards.find((card) => {
        const minimum = Number(card.dataset.minimum);
        const maximum = card.dataset.maximum ? Number(card.dataset.maximum) : Infinity;
        return quantity >= minimum && quantity <= maximum;
      }) || cards[0];

      cards.forEach((card) => {
        card.classList.toggle('is-active', card === matchingCard);
      });
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm?.querySelector('[name="add"]');

      this.currentVariant = null;
      this.updateVariantMetafieldContent(null);

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider.slideTo(0);

          this.productMedia.handleSlideChange();
        }
        if (this.productMedia.enableImageZoom) {
          this.productMedia.handlePhotoswipe();
        }
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = this.variantGroupImagesData.mapping.find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      selectedMedias(this.initialMedias).forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });
      selectedMedias(this.initialThumbs).forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      const quantities = this.quantityInputs.map((input) => input.value);

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });

      this.quantityInputs.forEach((input, index) => {
        const quantity = Number.parseInt(quantities[index], 10);
        const minimum = Number.parseInt(input.min, 10) || 1;
        const maximum = input.max ? Number.parseInt(input.max, 10) : Infinity;

        if (Number.isFinite(quantity) && quantity >= minimum && quantity <= maximum) {
          input.value = String(quantity);
        }
      });

      this.updateTotalPrice();
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}


// if (!customElements.get("product-info")) {
//   class ProductInfo extends HTMLElement {
//     abortController = undefined;
//     onVariantChangeUnsubscriber = undefined;
//     pendingRequestUrl = null;
//     preProcessHtmlCallbacks = [];
//     postProcessHtmlCallbacks = [];

//     constructor() {
//       super();

//       this.onWindowLoad = this.onWindowLoad.bind(this);
//     }

//     get productId() {
//       return this.getAttribute('data-product-id');
//     }

//     get sectionId() {
//       return this.dataset.originalSection || this.dataset.sectionId;
//     }

//     get disableSelectedVariantDefault() {
//       return this.dataset.disableSelectedVariantDefault === "true" || false;
//     }

//     get enableVariantGroupImages() {
//       return this.dataset.enableVariantGroupImages === "true" || false;
//     }

//     get enableHistoryState() {
//       return this.dataset.enableHistoryState === "true" || false;
//     }

//     get viewMode() {
//       return this.dataset.viewMode || 'main-product'
//     }

//     get pickupAvailability() {
//       return this.querySelector(`pickup-availability`);
//     }

//     get productForm() {
//       return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
//     }

//     get productMedia() {
//       return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
//     }

//     get variantPicker() {
//       return this.querySelector('variant-picker');
//     }

//     get quantityInput() {
//       return this.querySelector(`m-quantity-input input`);
//     }
//     get quantityInputStickyAtc() {
//       return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
//     }

//     get stickyAtc() {
//       return this.querySelector('sticky-atc');
//     }

//     connectedCallback() {
//       if (this.viewMode === "main-product") {
//         window.addEventListener("load", this.onWindowLoad);
//       }

//       this.init();
//     }

//     disconnectedCallback() {
//       window.removeEventListener("load", this.onWindowLoad);
//       this.onVariantChangeUnsubscriber();
//       this.cartUpdateUnsubscriber?.();
//     }

//     init() {
//       this.currentVariant = this.getSelectedVariant(this);
//       this.quantityInputs = [];

//       if (this.quantityInput) {
//         this.quantityInputs.push(this.quantityInput);
//       }
//       if (this.quantityInputStickyAtc) {
//         this.quantityInputs.push(this.quantityInputStickyAtc);
//       }

//       if (this.disableSelectedVariantDefault) {
//         this.handleDisableSelectedVariantDefault();
//       }

//       this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
//         MinimogTheme.pubSubEvents.optionValueSelectionChange,
//         this.handleOptionValueChange.bind(this)
//       );

//       this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

//       this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
//       this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

//       if (this.enableVariantGroupImages) {
//         this.variantGroupImagesData = this.getVariantGroupImagesData();
//         this.showFeaturedMedia = false;
//       };

//       this.initQuantityHandlers();

//       if (this.currentVariant && !this.showFeaturedMedia) {
//         this.updateMedia(this.currentVariant);
//       }
//     }

//     getVariantGroupImagesData() {
//       return (
//         JSON.parse(
//           this.querySelector('#variantGroup[type="application/json"]')
//             .textContent
//         ) || {}
//       );
//     }

//     handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
//       if (!this.contains(event.target)) return;

//       const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
//       const shouldSwapProduct = this.dataset.url !== productUrl;
//       // this.pendingRequestUrl = productUrl;
//       const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

//       this.renderProductInfo({
//         requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
//         targetId: target.id,
//         callback: shouldSwapProduct
//           ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
//           : this.handleUpdateProductInfo(productUrl, this.viewMode),
//       });
//     }

//     renderProductInfo({ requestUrl, targetId, callback }) {
//       this.abortController?.abort();
//       this.abortController = new AbortController();

//       fetch(requestUrl, { signal: this.abortController.signal })
//         .then((response) => response.text())
//         .then((responseText) => {
//           this.pendingRequestUrl = null;
//           const html = new DOMParser().parseFromString(responseText, 'text/html');
//           callback(html);
//         })
//         .then(() => {
//           // set focus to last clicked option value
//           document.querySelector(`#${targetId}`)?.focus();
//         })
//         .catch((error) => {
//           if (error.name === 'AbortError') {
//             console.log('Fetch aborted by user');
//           } else {
//             console.error(error);
//           }
//         });
//     }

//     buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
//       const params = [];

//       !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

//       if (optionValues.length) {
//         params.push(`option_values=${optionValues.join(',')}`);
//       }

//       return `${url}?${params.join('&')}`;
//     }

//     handleUpdateProductInfo(productUrl, viewMode) {
//       return (html) => {
//         const quickView = html.querySelector('#MainProduct-quick-view__content');
//         if (quickView && viewMode === 'quick-view') {
//           html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
//         }

//         const variant = this.getSelectedVariant(html);

//         this.pickupAvailability?.update(variant);
//         this.updateOptionValues(html);
//         this.updateURL(productUrl, variant?.id);
//         this.updateVariantInputs(variant?.id);

//         if (!variant) {
//           this.setUnavailable();
//           return;
//         }

//         this.updateMedia(variant);

//         const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
//           const source = html.getElementById(`${id}-${this.sectionId}`);
//           const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
//           if (source && destination) {
//             destination.innerHTML = source.innerHTML;
//             destination.classList.toggle('m:hidden', shouldHide(source));
//           }
//         };

//         updateSourceFromDestination('Price');
//         updateSourceFromDestination("Inventory");
//         updateSourceFromDestination("Sku");
//         updateSourceFromDestination("Availability");
//         updateSourceFromDestination("Volume");
//         updateSourceFromDestination('PricePerItem');

//         this.updateQuantityRules(this.sectionId, this.productId, html);
//         this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
//         this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

//         const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
//         this.toggleAddButton(
//           addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
//           window.MinimogStrings.soldOut
//         );

//         this.currentVariant = variant;

//         MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
//           data: { variant, sectionId: this.sectionId, html },
//         });

//         document.dispatchEvent(
//           new CustomEvent('variant:changed', {
//             detail: {
//               variant: this.currentVariant,
//             },
//           })
//         );
//       };
//     }

//     handleSwapProduct(productUrl, updateFullPage, viewMode) {
//       return (html) => {
//         const quickView = html.querySelector('#MainProduct-quick-view__content');
//         if (quickView && viewMode === 'quick-view') {
//           html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
//         }

//         const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
//         const productInfo = html.querySelector(selector);
//         const variant = this.getSelectedVariant(productInfo);
//         productInfo.dataset.disableSelectedVariantDefault = false;

//         this.updateURL(productUrl, variant?.id);

//         if (updateFullPage) {
//           document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
//           HTMLUpdateUtility.viewTransition(
//             document.querySelector('main'),
//             html.querySelector('main'),
//             this.preProcessHtmlCallbacks,
//             this.postProcessHtmlCallbacks
//           );
//         } else {
//           HTMLUpdateUtility.viewTransition(
//             this,
//             productInfo,
//             this.preProcessHtmlCallbacks,
//             this.postProcessHtmlCallbacks
//           );
//         }

//         this.currentVariant = variant;
//       };
//     }

//     getSelectedVariant(productInfoNode) {
//       const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
//       return !!selectedVariant ? JSON.parse(selectedVariant) : null;
//     }

//     updateURL(url, variantId) {
//       if (this.dataset.updateUrl === 'false') return;
//       window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
//     }

//     updateVariantInputs(variantId) {
//       document
//         .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
//         .forEach((productForm) => {
//           const input = productForm.querySelector('input[name="id"]');
//           input.value = variantId ?? '';
//           input.dispatchEvent(new Event('change', { bubbles: true }));
//         });
//     }

//     updateOptionValues(html) {
//       const variantPicker = html.querySelector('variant-picker');
//       if (variantPicker) {
//         HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
//       }
//     }

//     setUnavailable() {
//       const productForm = document.getElementById(
//         `product-form-${this.sectionId}`
//       );
//       const addButton = productForm.querySelector('[name="add"]');

//       if (addButton) {
//         this.toggleAddButton(true, window.MinimogStrings.unavailable);
//       }

//       const selectors = [
//         "Price",
//         "Inventory",
//         "Sku",
//         "Availability"
//       ]
//         .map((id) => `#${id}-${this.sectionId}`)
//         .join(", ");
//       document
//         .querySelectorAll(selectors)
//         .forEach((selector) => selector.classList.add('m:hidden'));
//     }

//     toggleAddButton(disable = true, text, modifyClass = true) {
//       const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
//       if (!productForm) return;
//       const addButton = productForm.querySelector('[name="add"]');
//       const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
//       if (!addButton) return;

//       if (disable) {
//         addButton.setAttribute('disabled', 'disabled');
//         if (text) addButtonText.textContent = text;
//       } else {
//         addButton.removeAttribute('disabled');
//         addButtonText.innerHTML = window.MinimogStrings.addToCart;
//       }

//       if (!modifyClass) return;
//     }

//     updateMedia(variant) {
//       if (!this.productMedia) return;
//       if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
//         this.handleVariantGroupImage(variant);

//         if (this.productMedia.slider) {
//           this.productMedia.slider.updateSlides();
//           this.productMedia.navSlider.updateSlides();

//           this.productMedia.slider.slideTo(0);
//           this.productMedia.navSlider.slideTo(0);

//           this.productMedia.handleSlideChange();
//         }
//         if (this.productMedia.enableImageZoom) {
//           this.productMedia.handlePhotoswipe();
//         }
//       } else {
//         const setActiveMedia = () => {
//           if (typeof this.productMedia.setActiveMedia === 'function') {
//             this.productMedia.init();
//             this.productMedia.setActiveMedia(variant);
//             return true; // Indicate success
//           }
//           return false; // Indicate failure
//         };

//         if (!setActiveMedia()) {
//           this.timer = setInterval(() => {
//             if (setActiveMedia()) {
//               clearInterval(this.timer);
//             }
//           }, 100);
//         }
//       }
//     }

//     handleDisableSelectedVariantDefault() {
//       let urlParams = new URLSearchParams(window.location.search);
//       if (urlParams.has("variant")) return;

//       this.pickerFields = this.querySelectorAll("[data-picker-field]");

//       this.pickerFields && this.pickerFields.forEach((field) => {
//         let pickerType = field.dataset.pickerField;
//         switch (pickerType) {
//           case "select":
//             let selectBox = field.querySelector("select");
//             let option = document.createElement("option");
//             option.text = this.dataset.variantOptionNoneText;
//             option.setAttribute("disabled", "");
//             option.setAttribute("selected", "");

//             selectBox.add(option, 0);
//             break;
//           default:
//             let checkedInputs = field.querySelectorAll("input:checked");
//             checkedInputs &&
//               checkedInputs.forEach(function (input) {
//                 input.removeAttribute("checked");
//               });
//             break;
//         }

//         field.dataset.selectedValue = "";
//         if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
//       });

//       this.setUnavailable();
//       this.currentVariant = null;
//     }

//     handleVariantGroupImage(variant) {
//       const selectedVariantData = this.variantGroupImagesData.mapping.find(
//         (item) => Number(item.id) === variant.id
//       );

//       const selectedMedias = (initialItems) => {
//         const selectedVariantMedias = Array.from(initialItems)
//           .map((media) => {
//             const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
//             const mediaType = media.dataset.mediaType;
//             if (index !== -1 || mediaType !== 'image') {
//               return media;
//             }
//             return null;
//           })
//           .filter(Boolean);

//         if (selectedVariantMedias.length <= 0) {
//           return Array.from(initialItems).map((media, index) => {
//             media.dataset.index = index;

//             media.removeAttribute('style'); // Avoid style of swipe slide item
//             media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

//             return media;
//           });
//         }

//         const orderedMedias = Object.values(selectedVariantData.media)
//           .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
//           .filter(Boolean);

//         const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

//         return Array.from([...orderedMedias, ...remainingMedias]).map((media, index) => {
//           media.dataset.index = index;

//           media.removeAttribute('style'); // Avoid style of swipe slide item
//           media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

//           return media;
//         });
//       }

//       const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
//       const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

//       const layout = this.productMedia.dataset.layout;

//       mediaWrapper.innerHTML = "";
//       thumbWrapper.innerHTML = "";

//       selectedMedias(this.initialMedias).forEach((media, index) => {
//         if (layout === "layout-2") {
//           media.classList.remove("m-col-span-2");
//           if (index % 3 === 0) {
//             media.classList.add("m-col-span-2");
//           }
//         }
//         mediaWrapper.append(media);
//       });
//       selectedMedias(this.initialThumbs).forEach((media) => {
//         media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
//         thumbWrapper.append(media);
//       });
//     }

//     initQuantityHandlers() {
//       if (this.quantityInputs.length <= 0) return;

//       this.setQuantityBoundries();
//       if (!this.hasAttribute("data-original-section-id")) {
//         this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
//           MinimogTheme.pubSubEvents.cartUpdate,
//           this.fetchQuantityRules.bind(this)
//         );
//       }
//     }

//     setQuantityBoundries() {
//       this.quantityInputs.forEach((input) => {
//         const quantityInputWrapper = input.closest('m-quantity-input');
//         quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
//       });
//     }

//     fetchQuantityRules() {
//       const currentVariantId = this.productForm?.variantIdInput?.value;
//       if (!currentVariantId) return;

//       this.querySelector('.quantity__rules-cart')?.classList.add('loading');

//       fetch(
//         `${this.getAttribute(
//           "data-url"
//         )}?variant=${currentVariantId}&section_id=${this.sectionId}`
//       )
//         .then((response) => response.text())
//         .then((responseText) => {
//           const parsedHTML = new DOMParser().parseFromString(
//             responseText,
//             "text/html"
//           );
//           this.updateQuantityRules(
//             this.sectionId,
//             this.productId,
//             parsedHTML
//           );
//         })
//         .catch((error) => {
//           console.error(error);
//         })
//         .finally(() => {
//           this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
//         });
//     }

//     updateQuantityRules(sectionId, productId, parsedHTML) {
//       if (this.quantityInputs.length <= 0) return;

//       this.quantityInputs.forEach((input) => {
//         const quantityInputWrapper = input.closest('m-quantity-input');
//         quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
//       });

//       this.setQuantityBoundries();
//     }

//     addRecentViewedProduct() {
//       const cookies = getCookie('m-recent-viewed-products')
//       let products = cookies ? JSON.parse(cookies) : []
//       if (products.indexOf(MinimogSettings.productHandle) === -1) {
//         products.unshift(MinimogSettings.productHandle)
//         products = products.slice(0, 20)
//         setCookie('m-recent-viewed-products', JSON.stringify(products));
//       }
//     }

//     onWindowLoad() {
//       this.acc = [];
//       MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
//       MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
//       this.addRecentViewedProduct();

//       addEventDelegate({
//         context: this,
//         selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
//         handler: (e) => {
//           const index = e.target.closest('.m-product-collapsible').dataset.index
//           setTimeout(() => {
//             this.acc[Number(index)].setContentHeight()
//           }, 300)
//         },
//         capture: true
//       })
//     }
//   }

//   customElements.define("product-info", ProductInfo);
// }